/* eslint-disable react/require-default-props */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import isEqual from 'lodash.isequal';
import InputGroup from './InputGroup';
import {
  Employee,
  EmployeeToBusiness,
  InputGroupType,
  LoadedUser,
  PayoutDistributionOptions,
} from '../../interfaces';
import './form.css';
import {
  CreateArtistValues,
  getArtistFormErrors,
  getInitFormValuesFromBusinessSettings,
  getInitFormValuesFromEmployee,
  getInputGroups,
  getValidationSchema,
} from '../../utils/form/artistForm';
import {
  onboardEmployee,
  updateEmployee,
  updateEmployeeOnschedResource,
} from '../../services/employee';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useUser from '../../hooks/global/useUser';
import {
  UpdateEmployeePayload,
  updateEmployeeSchema,
} from '../../types/employees';
import ArtistHoursInputGroup from './ArtistHoursInputGroup';
import useOnschedResource from '../../hooks/employees/useOnschedResource';
import {
  getRoleFromSelectedRoles,
  hasAvailabilityChanged,
} from '../../helpers/employees';
import useConfirm from '../../hooks/useConfirm';
import useGetBusinessSettings from '../../hooks/businesses/useGetBusinessSettings';
import useGetBusinessServices from '../../hooks/businesses/useGetBusinessServices';
import RolesHandler from './NewEditArtistRolesHandler';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';
import useGetGallery, {
  galleryQueryKey,
} from '../../hooks/gallery/useGetGallery';
import useGalleryUploads from '../gallery/useGalleryUploads';
import GallerySection from '../gallery/GallerySection';
import EditableInfoSection from './EditableInfoSection';
import { commitGallery } from '../../services/gallery';
import { GalleryContext } from '../../types/gallery';

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess?: () => void | Promise<void>;
  setStaffAdded: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedEmployee: React.Dispatch<
    React.SetStateAction<Employee | undefined>
  >;
  employee?: Employee;
  employeeToBusiness?: EmployeeToBusiness | null;
}

export default function NewEditArtistForm({
  setModalOpen,
  onSuccess,
  setStaffAdded,
  setSelectedEmployee,
  employee,
  employeeToBusiness,
}: Props) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const isEdit = !!employee;
  const validationSchema = React.useMemo(
    () => getValidationSchema(isEdit),
    [isEdit],
  );
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const { settings: businessSettings } = useGetBusinessSettings();
  const { services = [] } = useGetBusinessServices();
  const { reloadAfterUpdate } = useGetOnboarding();

  const galleryContext: GalleryContext | null = employee
    ? { kind: 'employee', employeeId: employee.employeeId }
    : null;
  const {
    images: gallery,
    isError: isGalleryError,
    isSuccess: isGallerySuccess,
    isLoading: isGalleryLoading,
    refetch: refetchGallery,
  } = useGetGallery(galleryContext);
  const galleryUploads = useGalleryUploads(galleryContext);

  const initialFormState = React.useMemo<CreateArtistValues>(() => {
    const baseValues = employee
      ? getInitFormValuesFromEmployee(
          employee,
          employeeToBusiness,
          services ?? [],
          isGallerySuccess ? gallery ?? [] : [],
        )
      : getInitFormValuesFromBusinessSettings(businessSettings);
    const timezoneId = !employee ? loadedUser?.timezone : baseValues.timezoneId;
    return {
      ...baseValues,
      timezoneId,
    };
  }, [
    businessSettings,
    employee,
    employeeToBusiness,
    gallery,
    isGallerySuccess,
    loadedUser?.timezone,
    services,
  ]);

  const [formError, setFormError] = React.useState<any>('');

  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();

  const { data: onschedResource, refetch } = useOnschedResource(
    employee?.employeeId || '',
    {
      enabled: !!employee?.employeeId,
    },
  );

  const onSaveAvailability = React.useCallback(
    async (employeeToUpdate: Employee, values: CreateArtistValues) => {
      if (!employeeToUpdate) return;
      if (employee && !onschedResource) return;
      try {
        const currentAvailability = onschedResource?.availability;

        const hasChanged =
          !currentAvailability ||
          hasAvailabilityChanged(
            currentAvailability,
            values.weeklyAvailability,
          );
        if (!hasChanged) return;

        const response = await updateEmployeeOnschedResource(
          employeeToUpdate.employeeId,
          { availability: values.weeklyAvailability },
        );

        if (!response) throw new Error('Error saving availability');

        await refetch();
      } catch (error: any) {
        throw new Error('Error saving availability');
      }
    },
    [employee, onschedResource, refetch],
  );

  const onSaveGallery = React.useCallback(
    async (employeeToUpdate: Employee, values: CreateArtistValues) => {
      if (!isGallerySuccess) return;

      const initialRefs = initialFormState.gallery.map(image => image.imageRef);
      const nextRefs = values.gallery.map(image => image.imageRef);
      if (isEqual(initialRefs, nextRefs)) return;

      const context: GalleryContext = {
        kind: 'employee',
        employeeId: employeeToUpdate.employeeId,
      };
      await commitGallery(context, nextRefs);
      await queryClient.invalidateQueries({
        queryKey: galleryQueryKey(context),
      });
    },
    [initialFormState.gallery, isGallerySuccess, queryClient],
  );

  const onCreateUser = React.useCallback(
    async (
      values: CreateArtistValues,
      { setSubmitting }: FormikHelpers<CreateArtistValues>,
    ) => {
      try {
        const artistResponse = await onboardEmployee(values, loadedUser);

        if (artistResponse) {
          await onSaveAvailability(artistResponse, values);
          reloadAfterUpdate('STAFF_ONBOARD');
          toast.success('Invite Sent');
          await onSuccess?.();
          setModalOpen(false);
          setStaffAdded(true);
          setSelectedEmployee(undefined);
        }
      } catch (error: any) {
        setFormError(
          'Error adding user. Please fill in all required fields and try again',
        );
        let errorMessage = 'Error adding user';
        if (
          typeof error.message === 'string' ||
          error.message instanceof String
        ) {
          errorMessage = getArtistFormErrors(error.message);
        }

        toast.error(errorMessage);
      } finally {
        closeLoadingModal();
        setSubmitting(false);
      }
    },
    [
      closeLoadingModal,
      loadedUser,
      onSaveAvailability,
      onSuccess,
      reloadAfterUpdate,
      setModalOpen,
      setSelectedEmployee,
      setStaffAdded,
    ],
  );

  const onEditUser = React.useCallback(
    async (
      values: CreateArtistValues,
      { setSubmitting }: FormikHelpers<CreateArtistValues>,
    ) => {
      if (!employee) return;
      try {
        const phoneNumber = !values.phoneNumber.startsWith('+')
          ? `+${values.phoneNumber}`
          : values.phoneNumber;

        const payrollType =
          values.payoutDistribution === PayoutDistributionOptions.COMMISSION
            ? 'COMMISSION'
            : 'OTHER';

        const payeeForOtherPayroll =
          values.payoutDistribution === PayoutDistributionOptions.BUSINESS
            ? 'BUSINESS'
            : 'EMPLOYEE';

        const data: UpdateEmployeePayload = {
          ...values,
          phoneNumber,
          employeeToBusiness: {
            businessId: loadedUser?.business.id,
            payrollType,
            addToAllChats: values.addToAllChats,
            canViewReports: values.canViewReports,
            canManageWaivers: values.canManageWaivers,
            canIssueRefunds: values.canIssueRefunds,
            canManageTeam: values.canManageTeam,
            canManageCampaigns: values.canManageCampaigns,
            generalRequestOwner: values.generalRequestOwner,
            canViewAllShopCustomers: values.canViewAllShopCustomers,
            isBookable: values.isBookable,
            payeeForOtherPayroll,
            color: values.color,
            defaultHourlyRate: values.defaultHourlyRate || null,
          },
        };
        if (values.timezoneId) data.timezone = values.timezoneId;
        if (
          values.commissionPercent &&
          data.employeeToBusiness &&
          payrollType === 'COMMISSION'
        ) {
          data.employeeToBusiness.commissionPercent =
            parseFloat(values.commissionPercent) / 100;
        }

        const body = await updateEmployeeSchema.noUnknown().validate(data, {
          stripUnknown: true,
        });

        const artistResponse = await updateEmployee(employee.employeeId, body);

        if (artistResponse) {
          await onSaveAvailability(artistResponse, values);
          try {
            await onSaveGallery(artistResponse, values);
          } catch {
            toast.error('User updated, but photos could not be saved.');
          }
          toast.success('User Updated');
          await onSuccess?.();
          setModalOpen(false);
          setSelectedEmployee(undefined);
        }
      } catch (error: any) {
        setFormError(
          'Error updating user. Please fill in all required fields and try again',
        );
        let errorMessage = 'Error updating user';
        if (
          typeof error.message === 'string' ||
          error.message instanceof String
        ) {
          errorMessage = getArtistFormErrors(error.message);
        }

        toast.error(errorMessage);
      } finally {
        closeLoadingModal();
        setSubmitting(false);
      }
    },
    [
      closeLoadingModal,
      employee,
      loadedUser?.business.id,
      onSaveAvailability,
      onSaveGallery,
      onSuccess,
      setModalOpen,
      setSelectedEmployee,
    ],
  );

  const onSubmit = React.useCallback(
    async (
      values: CreateArtistValues,
      helpers: FormikHelpers<CreateArtistValues>,
    ) => {
      if (!loadedUser) {
        setFormError('Please refresh and try again');
        toast.error('Error adding user');
        return null;
      }

      const isSelf = employee?.employeeId === loadedUser.userId;
      if (
        isSelf &&
        loadedUser.role === 'SHOP_MANAGER' &&
        getRoleFromSelectedRoles(values.selectedRoles) !== 'SHOP_MANAGER'
      ) {
        const confirmed = await confirm({
          title: 'Are you sure you want to change your role?',
          message:
            'You are about to remove yourself as a shop manager. You will no longer be able to manage the shop settings. As a shop manager, you can still use the app as an artist or piercer, you just have additional permissions to also manage the shop.',
        });
        if (!confirmed) return null;
      }

      const header = isEdit ? 'Updating User...' : 'Creating User...';
      updateLoadingModal({
        header,
        message: 'This could take a minute. Please do not refresh the page.',
        status: 'loading',
      });

      const response = isEdit
        ? await onEditUser(values, helpers)
        : await onCreateUser(values, helpers);
      return response;
    },
    [
      loadedUser,
      employee?.employeeId,
      isEdit,
      updateLoadingModal,
      onEditUser,
      onCreateUser,
      confirm,
    ],
  );

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={validationSchema}
        onSubmit={onSubmit}>
        {({
          isSubmitting,
          values,
          isValid,
          dirty: formikDirty,
          setFieldValue,
        }) => {
          const inputGroups = getInputGroups({
            employee,
            values,
            currentUser: loadedUser,
          });
          const hasAvailChanged =
            !onschedResource?.availability ||
            hasAvailabilityChanged(
              onschedResource.availability,
              values.weeklyAvailability,
            );
          const dirty = formikDirty || hasAvailChanged;
          if (!inputGroups?.length) return null;

          return (
            <Form>
              <RolesHandler />
              {formError && (
                <Typography variant="body1" color="primary">
                  {formError}
                </Typography>
              )}
              {inputGroups.map((inputGroup: InputGroupType) => (
                <InputGroup
                  name={inputGroup.name}
                  rows={inputGroup.inputRows}
                  key={inputGroup.name}
                />
              ))}
              <ArtistHoursInputGroup employee={employee} />
              {employee && (
                <EditableInfoSection
                  title="Gallery"
                  description="Saves with the rest of this user's changes.">
                  {isGalleryError ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                      }}>
                      <Typography variant="body2" color="text.secondary">
                        Could not load photos.
                      </Typography>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => refetchGallery()}>
                        Retry
                      </Button>
                    </Box>
                  ) : isGalleryLoading || !isGallerySuccess ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading photos…
                    </Typography>
                  ) : (
                    <GallerySection
                      images={values.gallery}
                      onChange={images => setFieldValue('gallery', images)}
                      uploads={galleryUploads}
                      disabled={isSubmitting}
                      dirty={
                        !isEqual(
                          values.gallery.map(image => image.imageRef),
                          (gallery ?? []).map(image => image.imageRef),
                        )
                      }
                      leadCount={1}
                      tileMinWidth={148}
                      helpText="Photos of their work, in the order clients see them. Drag to reorder, and the first photo leads their profile page."
                      emptyDetail="Add photos customers will see on their page. The first one leads."
                    />
                  )}
                </EditableInfoSection>
              )}
              <Button
                type="submit"
                disabled={
                  !isValid ||
                  isSubmitting ||
                  !values.firstName ||
                  !values.lastName ||
                  !values.email ||
                  !values.phoneNumber ||
                  !values.displayName ||
                  !values.urlPath ||
                  !values.payoutDistribution ||
                  !dirty ||
                  (values.payoutDistribution === 'COMMISSION' &&
                    !values.commissionPercent)
                }
                variant="contained"
                className="primary">
                {isEdit ? 'Save' : 'Send Invite'}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}
